import { User, XCircle } from 'lucide-react'
import { formatDate } from '@/utils/format-date'
import { getDealAvatarFallback } from '@/utils/dealDisplay'
import type { Deal } from '@/types'
import { CompanyDataSection } from './CompanyDataSection'
import { QualificacaoSection } from './QualificacaoSection'
import { LegalDataSection } from './LegalDataSection'
import { AgendarReuniaoSection } from './AgendarReuniaoSection'
import { ReuniaoAgendadaSection } from './ReuniaoAgendadaSection'
import { ReuniaoRealizadaSection } from './ReuniaoRealizadaSection'
import { HandoffBdrSection } from './HandoffBdrSection'
import { ObservacoesSection } from './ObservacoesSection'
import { DealWinDateSection } from './DealWinDateSection'
import { PortalLinkCard } from './PortalLinkCard'
import { DealValueSummary } from './DealValueSummary'
import { DealPartnerCommissionSection } from './DealPartnerCommissionSection'
import { isRocketInstance } from '@/config/instanceProfile'
import type { ReuniaoFooterAction } from './useDealDetailPanel'

function DealInfoStageSections({
  deal,
  onDealPatched,
  highlightMeetingDate,
  highlightBant,
  onRegisterReuniaoConfirm,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
  highlightMeetingDate: boolean
  highlightBant: boolean
  onRegisterReuniaoConfirm?: (action: ReuniaoFooterAction | null) => void
}) {
  if (deal.etapa === 'qualificado') {
    return (
      <AgendarReuniaoSection
        deal={deal}
        onDealPatched={onDealPatched}
        highlightMeetingDate={highlightMeetingDate}
      />
    )
  }
  if (deal.etapa === 'reuniao_agendada') {
    return (
      <ReuniaoAgendadaSection
        deal={deal}
        onDealPatched={onDealPatched}
        highlightBant={highlightBant}
        onRegisterConfirm={onRegisterReuniaoConfirm}
      />
    )
  }
  if (deal.etapa === 'reuniao_realizada') {
    return <ReuniaoRealizadaSection deal={deal} onDealPatched={onDealPatched} />
  }
  return null
}

export function DealInfoTab({
  deal,
  onDealPatched,
  highlightFields = [],
  onRegisterReuniaoConfirm,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
  highlightFields?: string[]
  onRegisterReuniaoConfirm?: (action: ReuniaoFooterAction | null) => void
}) {
  const highlightMeetingDate = highlightFields.includes('dataReuniao')
  const bantFieldKeys = ['budgetConfirmado', 'decisorPresente', 'doresIdentificadas'] as const
  const highlightBant = bantFieldKeys.some((field) => highlightFields.includes(field))

  return (
    <div className="space-y-4">
      <DealInfoStageSections
        deal={deal}
        onDealPatched={onDealPatched}
        highlightMeetingDate={highlightMeetingDate}
        highlightBant={highlightBant}
        onRegisterReuniaoConfirm={onRegisterReuniaoConfirm}
      />

      <DealValueSummary deal={deal} />

      {isRocketInstance && <DealPartnerCommissionSection deal={deal} />}

      <HandoffBdrSection deal={deal} />

      <CompanyDataSection deal={deal} onDealPatched={onDealPatched} />

      {['pausado', 'proposta_elaboracao', 'proposta_enviada', 'renegociacao', 'gerar_contrato', 'contrato_enviado', 'handover', 'ganho'].includes(
        deal.etapa,
      ) && <QualificacaoSection deal={deal} onDealPatched={onDealPatched} />}

      {(deal.etapa === 'gerar_contrato' ||
        deal.etapa === 'contrato_enviado' ||
        deal.etapa === 'ganho') && (
        <LegalDataSection deal={deal} onDealPatched={onDealPatched} />
      )}

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-3">
        <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-text-label">
          <User size={14} className="text-accent" />
          Closer responsável
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/10">
            <span className="text-sm font-semibold text-accent">
              {getDealAvatarFallback(deal.closerNome, deal.closerAvatar)}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-text-primary">{deal.closerNome}</p>
            <p className="text-xs text-text-label">
              {deal.driftDays > 0 ? `${deal.driftDays} dias no pipeline` : 'Recém-criado'}
            </p>
          </div>
        </div>
      </div>

      <ObservacoesSection deal={deal} onDealPatched={onDealPatched} />

      {deal.motivoPerda && (
        <div className="glass-card space-y-3 border-l-2 border-l-red-500/60 p-4">
          <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-400">
            <XCircle size={14} />
            Motivo da perda
          </h3>
          <div className="space-y-2">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-text-label">Motivo</span>
              <p className="text-sm text-text-secondary">{deal.motivoPerda}</p>
            </div>
            {deal.submotivoPerda && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-text-label">Submotivo</span>
                <p className="text-sm text-text-secondary">{deal.submotivoPerda}</p>
              </div>
            )}
            {deal.comentarioPerda && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-text-label">Comentário</span>
                <p className="text-sm text-text-secondary">{deal.comentarioPerda}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {deal.etapa === 'ganho' && (
        <>
          <DealWinDateSection deal={deal} onDealPatched={onDealPatched} />
          <PortalLinkCard deal={deal} />
        </>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-label">
        <span>Criado: {formatDate(deal.createdAt)}</span>
        <span>Atualizado: {formatDate(deal.updatedAt)}</span>
      </div>
    </div>
  )
}
